import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaMobile, FaLaptopCode, FaCubes, FaRobot, FaShoppingCart, FaServer, FaArrowRight } from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: FaLaptopCode,
      title: 'Web Development',
      description:
        'Custom web applications built with cutting-edge technologies for optimal performance and scalability.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      icon: FaMobile,
      title: 'Mobile Development',
      description:
        'Native and cross-platform mobile apps for iOS and Android that deliver exceptional user experiences.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      icon: FaCubes,
      title: 'Blockchain Development',
      description: 'Decentralized applications and smart contracts built on cutting-edge blockchain technology.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      icon: FaRobot,
      title: 'AI & Machine Learning',
      description: 'Intelligent automation and ML-powered solutions to give your business a competitive edge.',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
      icon: FaShoppingCart,
      title: 'E-Commerce',
      description: 'Full-featured e-commerce platforms with seamless payment integration and inventory management.',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    },
    {
      icon: FaServer,
      title: 'SaaS Product Development',
      description: 'End-to-end SaaS solutions with scalable architecture, subscription management, and multi-tenancy.',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section className="services" id="services" ref={ref}>
      <div className="services-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: '1rem' }}
        >
          <span className="section-tag">What We Offer</span>
          <h2 className="section-title">Our Services</h2>
          <p className="section-description">Comprehensive solutions tailored to your business needs</p>
        </motion.div>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={itemVariants}
              whileHover={{ y: -15, transition: { duration: 0.3 } }}
            >
              <div className="service-icon-wrapper">
                <div className="service-icon-bg" style={{ background: service.gradient }}></div>
                <div className="service-icon">
                  <service.icon />
                </div>
              </div>

              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <Link to="/services">
                <motion.button className="service-btn" whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                  Learn More →
                </motion.button>
              </Link>

              <div className="card-border" style={{ background: service.gradient }}></div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="view-all-container"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Link to="/services">
            <motion.button className="view-all-button" whileHover={{ scale: 1.05, x: 5 }} whileTap={{ scale: 0.95 }}>
              View All Services
              <FaArrowRight className="arrow-icon" />
            </motion.button>
          </Link>
        </motion.div>

        <motion.div
          className="services-cta"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h3>Need a custom solution?</h3>
          <p>Let's discuss how we can help you achieve your goals</p>
          <Link to="/contact">
            <motion.button className="cta-button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              Schedule a Consultation
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
